import { Injectable } from '@angular/core';
import { TenantService } from '../tenant/tenant.service';
import { RestTmtService } from '../rest-tmt.service';

@Injectable({
    providedIn: 'root'
})
export class PwaManifestService {
    private currentManifestUrl: string | null = null;
    private readonly PWA_TENANT_URL_KEY = 'pwa_tenant_url';
    private readonly PWA_TENANT_ID_KEY = 'pwa_tenant_id';

    constructor(private tenantService: TenantService, private serviceTmT: RestTmtService) {
        this.storeCurrentUrl();
        this.registerCustomServiceWorker();
        // Aggiungi il manifest dinamico all'avvio
        // Usa setTimeout per assicurarsi che il DOM sia pronto
        setTimeout(() => {
            this.serveDynamicManifest();
        }, 100);
    }

    /**
     * Genera l'array delle icone per il manifest
     * Default: assets/icons - Override SOLO se presente pwa.icons nel tenant.json
     */
    private generateIconsArray(): any[] {
        const clientTenant = this.serviceTmT.getClient();

        // Solo le dimensioni che esistono effettivamente
        const iconSizes = [72, 96, 128, 144, 152, 192, 384, 512];
        const maskableSizes = [192, 512];
        const icons: any = [];

        // Determina il percorso base delle icone
        // Default: /assets/icons - Override SOLO se presente pwa.icons
        let iconBasePath = '/assets/icons';
        let useCustomIcons = false;

        if (clientTenant && clientTenant.pwa && clientTenant.pwa.icons) {
            iconBasePath = clientTenant.pwa.icons;
            if (!iconBasePath.startsWith('/')) {
                iconBasePath = '/' + iconBasePath;
            }
            useCustomIcons = true;
            console.log(`PWA: Usando percorso icone custom da tenant.json: ${iconBasePath}`);
        } else {
            console.log(`PWA: Usando icone default: ${iconBasePath}`);
        }

        // Genera icone con purpose appropriato e URL assoluti
        iconSizes.forEach(size => {
            // Crea l'URL dell'icona con fallback
            // Assicuriamoci che il percorso non finisca con / se lo aggiungiamo qui
            const cleanBasePath = iconBasePath.endsWith('/') ? iconBasePath.slice(0, -1) : iconBasePath;
            const customIconUrl = `${window.location.origin}${cleanBasePath}/icon-${size}x${size}.png`;
            const defaultIconUrl = `${window.location.origin}/assets/icons/icon-${size}x${size}.png`;

            const iconBase = {
                "src": useCustomIcons ? customIconUrl : defaultIconUrl,
                "sizes": `${size}x${size}`,
                "type": "image/png"
            };

            // Icona standard
            icons.push({
                ...iconBase,
                "purpose": "any"
            });

            // Icona maskable solo per dimensioni specifiche
            if (maskableSizes.includes(size)) {
                icons.push({
                    ...iconBase,
                    "purpose": "maskable"
                });
            }
        });

        console.log(`PWA: Icone generate da percorso: ${iconBasePath}`);
        return icons;
    }

    /**
     * Genera dinamicamente il manifest.webmanifest basato sul tenant corrente
     */
    generateDynamicManifest(): any {
        const clientTenant = this.serviceTmT.getClient();
        const currentUrl = window.location.href;
        console.log('PWA: URL corrente:', currentUrl);

        // Estrai l'origine (protocol + host) per il scope
        const urlObj = new URL(currentUrl);
        // Scope semplice e sicuro: root del dominio
        const scope = urlObj.origin + '/';

        // Start URL assoluto per evitare errori di validazione
        const startUrl = currentUrl;

        console.log('PWA: Scope impostato:', scope);
        console.log('PWA: Start URL relativo:', startUrl);
        console.log('PWA: URL completo:', currentUrl);

        // Debug per verificare compatibilità scope/start_url
        const resolvedStartUrl = new URL(startUrl, scope).href;
        console.log('PWA: Start URL risolto:', resolvedStartUrl);
        console.log('PWA: Start URL è nel scope?', resolvedStartUrl.startsWith(scope));

        // Verifica requisiti PWA per installabilità
        console.log('PWA: Protocollo HTTPS?', window.location.protocol === 'https:');
        console.log('PWA: Service Worker registrato?', 'serviceWorker' in navigator);
        console.log('PWA: Display mode standalone?', true); // sempre true nel manifest

        // Controlli aggiuntivi per debugging
        console.log('PWA: User Agent:', navigator.userAgent);
        console.log('PWA: È mobile?', /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent));
        console.log('PWA: Manifest link presente?', !!document.querySelector('link[rel="manifest"]'));

        // Verifica che il manifest sia valido
        setTimeout(() => {
            if ('getInstalledRelatedApps' in navigator) {
                (navigator as any).getInstalledRelatedApps().then((apps: any[]) => {
                    console.log('PWA: App già installate:', apps);
                });
            }
        }, 1000);

        // Estrai bookID per creare un ID univoco per ogni installazione
        const bookId = this.getBookIdFromUrl();
        // L'ID deve essere univoco per permettere installazioni multiple di preventivi diversi
        const uniqueId = bookId ? `${scope}bookID/${bookId}` : scope;

        const baseManifest: any = {
            "name": "MyTravelPlan",
            "short_name": "MyTravelPlan",
            "description": "Applicazione PWA multitenant",
            "theme_color": "#1976d2",
            "background_color": "#fafafa",
            "display": "standalone",
            "orientation": "portrait-primary",
            "scope": scope,
            "start_url": startUrl,
            "icons": this.generateIconsArray(),
            "categories": ["business", "productivity"],
            "lang": "it",
            "id": uniqueId,
            "prefer_related_applications": false,
            "edge_side_panel": {
                "preferred_width": 400
            }
        };

        // console log per verificare i dati del tenant
        console.log('PWA: Dati tenant per manifest:', clientTenant);

        // Personalizza il manifest basato sui dati del tenant
        // Il nome viene sovrascritto SOLO se è presente l'oggetto pwa nel tenant.json
        if (clientTenant && clientTenant.pwa) {
            if (clientTenant.pwa.appName) {
                baseManifest.name = clientTenant.pwa.appName;
                console.log(`PWA: Nome app personalizzato: ${clientTenant.pwa.appName}`);
            }
            if (clientTenant.pwa.shortName) {
                baseManifest.short_name = clientTenant.pwa.shortName;
                console.log(`PWA: Nome breve personalizzato: ${clientTenant.pwa.shortName}`);
            }
        }

        console.log('PWA: Manifest generato:', baseManifest);
        return baseManifest;
    }

    /**
     * Memorizza la URL corrente per l'uso offline
     */
    storeCurrentUrl(): void {
        const currentUrl = window.location.href;
        localStorage.setItem(this.PWA_TENANT_URL_KEY, currentUrl);
        localStorage.setItem(this.PWA_TENANT_ID_KEY, this.tenantService.getTenant());
        console.log('PWA: URL corrente memorizzata:', currentUrl);
    }

    /**
     * Recupera la URL memorizzata per il tenant
     */
    getStoredUrl(): string | null {
        return localStorage.getItem(this.PWA_TENANT_URL_KEY);
    }

    /**
     * Recupera il tenant ID memorizzato
     */
    getStoredTenantId(): string | null {
        return localStorage.getItem(this.PWA_TENANT_ID_KEY);
    }

    /**
     * Serve dinamicamente il manifest come Blob URL
     * Il manifest viene generato interamente da Angular (che ha accesso al tenant.json)
     * e iniettato come Blob URL nel DOM - NON passa dal Service Worker
     */
    serveDynamicManifest(): void {
        // Genera il manifest completo con tutti i dati del tenant
        const manifest = this.generateDynamicManifest();

        // Pulisci vecchio Blob URL se esistente
        this.cleanup();

        // Crea Blob URL dal manifest generato
        const blob = new Blob([JSON.stringify(manifest)], { type: 'application/json' });
        this.currentManifestUrl = URL.createObjectURL(blob);

        // Rimuovi eventuali manifest link esistenti e aggiungi il nuovo
        this.removeExistingManifest();
        this.addManifestLink(this.currentManifestUrl);

        console.log('PWA: Manifest Blob URL generato:', manifest.name, manifest.short_name);
        console.log('PWA: Icons path:', manifest.icons?.[0]?.src);
        console.log('PWA: Start URL:', manifest.start_url);
        console.log('PWA: ID:', manifest.id);

        this.storeCurrentUrl();
    }

    /**
     * Estrae il bookID dall'URL corrente
     */
    getBookIdFromUrl(): string | null {
        const path = window.location.pathname;
        const parts = path.split('/');
        const bookIdIndex = parts.indexOf('bookID');
        if (bookIdIndex !== -1 && parts[bookIdIndex + 1]) {
            return parts[bookIdIndex + 1];
        }

        // Cerca anche tra i parametri query
        const urlParams = new URLSearchParams(window.location.search);
        return urlParams.get('bookid') || urlParams.get('bookID');
    }


    /**
     * Rimuove il manifest esistente se presente
     * Rimuove TUTTI i link manifest presenti per evitare conflitti
     */
    private removeExistingManifest(): void {
        const existingManifests = document.querySelectorAll('link[rel="manifest"]');
        existingManifests.forEach(manifest => manifest.remove());
    }

    /**
     * Aggiunge il link del manifest al DOM
     */
    private addManifestLink(manifestUrl: string): void {
        const link = document.createElement('link');
        link.rel = 'manifest';
        link.href = manifestUrl;
        document.head.appendChild(link);
    }

    /**
     * Pulisce le risorse del manifest dinamico
     */
    cleanup(): void {
        if (this.currentManifestUrl) {
            URL.revokeObjectURL(this.currentManifestUrl);
            this.currentManifestUrl = null;
        }
    }

    /**
     * Verifica se l'app è in modalità standalone (installata)
     */
    isStandalone(): boolean {
        return window.matchMedia('(display-mode: standalone)').matches ||
            (window.navigator as any).standalone === true;
    }

    /**
     * Gestisce la navigazione offline verificando il tenant memorizzato
     */
    handleOfflineNavigation(): void {
        if (this.isStandalone()) {
            const storedTenantId = this.getStoredTenantId();
            const currentTenant = this.tenantService.getTenant();

            if (storedTenantId && storedTenantId !== currentTenant) {
                console.warn('PWA: Tenant mismatch detected. Stored:', storedTenantId, 'Current:', currentTenant);
                // Potresti voler reindirizzare alla URL memorizzata o mostrare un avviso
                const storedUrl = this.getStoredUrl();
                if (storedUrl) {
                    console.log('PWA: Redirecting to stored URL:', storedUrl);
                    window.location.href = storedUrl;
                }
            }
        }
    }

    /**
     * Registra il service worker personalizzato per gestire il caching offline
     */
    private async registerCustomServiceWorker(): Promise<void> {
        if ('serviceWorker' in navigator) {
            try {
                console.log('PWA: Registrando service worker personalizzato...');
                const registration = await navigator.serviceWorker.register('/custom-sw.js', {
                    scope: '/'
                });

                console.log('PWA: Service worker personalizzato registrato:', registration);

                // Ascolta gli aggiornamenti del service worker
                registration.addEventListener('updatefound', () => {
                    console.log('PWA: Nuovo service worker trovato');
                    const newWorker = registration.installing;
                    if (newWorker) {
                        newWorker.addEventListener('statechange', () => {
                            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                                console.log('PWA: Nuovo service worker installato, ricarica consigliata');
                            }
                        });
                    }
                });

            } catch (error) {
                console.error('PWA: Errore registrazione service worker personalizzato:', error);
            }
        } else {
            console.warn('PWA: Service Worker non supportato');
        }
    }
}